import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Plus, Upload, X } from 'lucide-react'
import { supabase } from '@/lib/supabase/client'
import { generatePDF } from '@/services/mtPDFTemplate'
import { getCompanyNDTSettings, getDefaultNDTSettings, getNDTTestProcedures } from '@/services/ndtSettings.service'
import type { CompanyNDTSettingsDetailed, NDTTestProcedure } from '@/types/ndtSettings.types'
import {
  DEFAULT_MT_EQUIPMENT,
  MT_DEFAULT_TEST_SITE,
  MT_TEST_SITES,
  applyMpiConsumables,
  type MTFormData,
  type MTMpiType,
} from '@/types/mtTypes'
import { generateOrderNumber, getNextMethodReportSequence } from '@/utils/getNextReportSequence'
import { getNdtExitPath } from '@/utils/ndtNavigation'
import { completeJobAssignmentsForEquipment, NDT_REPORT_WORK_TYPES } from '@/services/jobs.service'
import { applySelectedEquipmentToForm, attachNdtReportToEquipment } from '@/utils/ndtEquipmentReport'
import { useNdtPersonnel } from '@/hooks/useNdtPersonnel'
import NdtFormPage, { NdtFormActions, NdtStatusButtons, ndtInputCls, ndtLabelCls, ndtSelectCls, ndtTextareaCls, ndtToggleCls, ndtUploadCls, ndtPrimaryBtnCls, ndtSecondaryBtnCls } from '@/components/ndt/NdtFormPage'
import NdtEquipmentSearch, { type NdtEquipmentHit } from '@/components/ndt/NdtEquipmentSearch'

const MTTestForm = () => {
  const navigate = useNavigate()
  const { inspectors, admins, companies, currentUser, loading, isAdmin, isInspectorLocked } = useNdtPersonnel()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isGeneratingPreview, setIsGeneratingPreview] = useState(false)
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [companySettings, setCompanySettings] = useState<CompanyNDTSettingsDetailed | null>(null)
  const [availableProcedures, setAvailableProcedures] = useState<NDTTestProcedure[]>([])
  const [showDrawing, setShowDrawing] = useState(false)
  const [selectedEquipmentId, setSelectedEquipmentId] = useState<string | null>(null)

  const [formData, setFormData] = useState<MTFormData>({
    testDate: new Date().toISOString().split('T')[0],
    testSite: MT_DEFAULT_TEST_SITE,
    reportNumber: '',
    objects: [''],
    objectDescription: '',
    issuedBy: '',
    verifiedBy: '',
    status: 'Accepted',
    surfaceCondition: 'Cleaned',
    equipmentSettings: applyMpiConsumables({ ...DEFAULT_MT_EQUIPMENT }, 'mpi'),
    extentOfExamination: '100',
    extentOfExaminationType: 'percentage',
    material: 'Carbon steel',
    mpiType: 'mpi',
    drawing: '',
    diameter: '',
    thickness: '',
    customerProject: '',
    hullYard: '',
    charge: '',
    testProcedure: 'MT DNV-CG-0051',
    testAcceptance: 'DNV-CG-0051 Level 1',
    referenceStandards: '---',
    comments: '',
    company: '',
    report_code: null,
    attachedImage: undefined,
  })

  useEffect(() => {
    if (isInspectorLocked && currentUser?.full_name && !formData.issuedBy) {
      setFormData((prev) => ({ ...prev, issuedBy: currentUser.full_name }))
    }
  }, [isInspectorLocked, currentUser, formData.issuedBy])

  const customFields = companySettings?.custom_fields?.object_description_format
  const showCustomFields = !!(
    customFields?.require_customer_project ||
    customFields?.require_hull_yard ||
    customFields?.require_charge
  )

  const canSubmit = useMemo(() => {
    if (!formData.reportNumber.trim() || !formData.issuedBy.trim()) return false
    if (!formData.objectDescription.trim()) return false
    return true
  }, [formData])

  const selectedCompanyName = useMemo(
    () => companies.find((c) => c.id === formData.company)?.name ?? '',
    [companies, formData.company],
  )

  const handleSelectEquipment = (equipment: NdtEquipmentHit | null) => {
    setSelectedEquipmentId(equipment?.id ?? null)
    if (!equipment) return
    setFormData((prev) => applySelectedEquipmentToForm(prev, equipment))
  }

  const handleCompanyChange = async (companyId: string) => {
    const selected = companies.find((c) => c.id === companyId)
    const reportCode = selected?.report_code || ''
    const orderNumber = reportCode ? generateOrderNumber(reportCode) : ''
    setSelectedEquipmentId(null)

    let settings: CompanyNDTSettingsDetailed | null = null
    let procedures: NDTTestProcedure[] = []

    if (companyId) {
      settings = await getCompanyNDTSettings(companyId, 'MT')
      setCompanySettings(settings)
      procedures = await getNDTTestProcedures('MT')
      setAvailableProcedures(procedures)
    } else {
      setCompanySettings(null)
      setAvailableProcedures([])
    }

    let procedureName = 'MT DNV-CG-0051'
    let acceptanceCriteria = 'DNV-CG-0051 Level 1'
    let referenceStandards = '---'

    if (settings) {
      procedureName = settings.default_procedure_name || procedureName
      acceptanceCriteria = settings.default_acceptance_criteria || acceptanceCriteria
      referenceStandards = settings.default_reference_standards || referenceStandards
    } else if (!companyId) {
      const defaults = await getDefaultNDTSettings('MT')
      if (defaults.default_procedure_id) {
        const { data: proc } = await supabase
          .from('ndt_test_procedures')
          .select('procedure_name')
          .eq('id', defaults.default_procedure_id)
          .single()
        if (proc) procedureName = proc.procedure_name
      }
      if (defaults.default_acceptance_id) {
        const { data: acc } = await supabase
          .from('ndt_test_acceptance')
          .select('acceptance_criteria')
          .eq('id', defaults.default_acceptance_id)
          .single()
        if (acc) acceptanceCriteria = acc.acceptance_criteria
      }
    }

    setFormData((prev) => ({
      ...prev,
      company: companyId,
      report_code: reportCode || null,
      reportNumber: orderNumber || prev.reportNumber,
      testProcedure: procedureName,
      testAcceptance: acceptanceCriteria,
      referenceStandards,
      hullYard: '',
      customerProject: '',
      charge: '',
    }))
  }

  const handleMpiTypeChange = (mpiType: MTMpiType) => {
    setFormData((prev) => ({
      ...prev,
      mpiType,
      equipmentSettings: applyMpiConsumables(prev.equipmentSettings, mpiType),
    }))
  }

  const handleObjectChange = (index: number, value: string) => {
    setFormData((prev) => {
      const objects = [...prev.objects]
      objects[index] = value
      return { ...prev, objects }
    })
  }

  const buildPdfPayload = useCallback(
    (sequenceNumber: string) => {
      const filteredObjects = formData.objects.filter((obj) => obj.trim())
      const objectsForReport = filteredObjects.length > 0 ? filteredObjects : [formData.objectDescription]
      return {
        ...formData,
        reportNumber: formData.reportNumber.trim().replace(/^MT-/i, ''),
        objects: objectsForReport,
        certificationNumber: '---',
        sequenceNumber,
        verifiedBy: formData.verifiedBy,
        approvedBy: formData.verifiedBy || undefined,
        approvedAt: formData.verifiedBy ? formData.testDate : undefined,
      }
    },
    [formData],
  )

  const closePreview = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl)
    setPreviewUrl(null)
  }

  const handlePreview = async () => {
    setIsGeneratingPreview(true)
    try {
      const { sequenceNumber } = await getNextMethodReportSequence('MT', formData.reportNumber)
      const blob = await generatePDF(buildPdfPayload(sequenceNumber))
      closePreview()
      setPreviewUrl(URL.createObjectURL(blob))
    } catch (error) {
      console.error(error)
      alert('Kunne ikke generere forhåndsvisning.')
    } finally {
      setIsGeneratingPreview(false)
    }
  }

  const handleSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault()
    if (!canSubmit) {
      alert('Vennligst fyll ut alle påkrevde felt.')
      return
    }

    setIsSubmitting(true)
    try {
      const filteredObjects = formData.objects.filter((obj) => obj.trim())
      const objectsForReport = filteredObjects.length > 0 ? filteredObjects : [formData.objectDescription]
      const { storageFolder, sequenceNumber, reportId } = await getNextMethodReportSequence('MT', formData.reportNumber)
      const folderPath = `${storageFolder}/${reportId}.pdf`
      const pdfBlob = await generatePDF(buildPdfPayload(sequenceNumber))

      const { error: uploadError } = await supabase.storage
        .from('reports')
        .upload(folderPath, pdfBlob, { contentType: 'application/pdf', cacheControl: '3600' })
      if (uploadError) throw uploadError

      const { data: { user } } = await supabase.auth.getUser()
      const { attachedImage: _omit, ...stored } = formData
      const insertData: Record<string, unknown> = {
        order_number: reportId,
        unit_id: objectsForReport.join(', '),
        test_date: formData.testDate,
        test_type: 'MT',
        status: formData.status,
        issued_by: formData.issuedBy,
        verified_by: formData.verifiedBy?.trim() || null,
        file_path: folderPath,
        report_code: formData.report_code,
        created_by: user?.id,
        report_data: { ...stored, objects: objectsForReport, sequenceNumber, equipmentId: selectedEquipmentId },
      }
      if (formData.company?.trim()) insertData.company_id = formData.company
      if (selectedEquipmentId) insertData.equipment_id = selectedEquipmentId

      const { error: metadataError } = await supabase.from('ndt_reports').insert(insertData)
      if (metadataError) throw metadataError

      if (selectedEquipmentId) {
        await attachNdtReportToEquipment({
          pdfBlob,
          fileName: `${reportId}.pdf`,
          customerName: selectedCompanyName,
          serialNumber: objectsForReport[0] || reportId,
          equipmentId: selectedEquipmentId,
          testDate: formData.testDate,
          reportStatus: formData.status,
        })
      }

      const { data: signed } = await supabase.storage.from('reports').createSignedUrl(folderPath, 60)
      if (signed?.signedUrl) window.open(signed.signedUrl, '_blank')

      alert(selectedEquipmentId
        ? 'Rapport generert, lagret på utstyr og NDT-datoer oppdatert.'
        : 'Rapport generert og lagret.')
      if (selectedEquipmentId) void completeJobAssignmentsForEquipment(selectedEquipmentId, NDT_REPORT_WORK_TYPES)
      navigate(getNdtExitPath())
    } catch (error) {
      console.error(error)
      alert(error instanceof Error ? error.message : 'Feil ved generering av rapport. Prøv igjen.')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    const reader = new FileReader()
    reader.onloadend = () => setFormData((prev) => ({ ...prev, attachedImage: reader.result as string }))
    reader.readAsDataURL(file)
  }

  return (
    <NdtFormPage
      highlight={formData.mpiType === 'fluorescent' ? 'Fluorescent Magnetic Particle' : 'Magnetic Particle'}
      title="Testing"
      loading={loading}
      previewUrl={previewUrl}
      previewTitle="MT-rapport forhåndsvisning"
      onClosePreview={closePreview}
      onPublish={() => void handleSubmit()}
      isPublishing={isSubmitting}
      canPublish={canSubmit}
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => handleMpiTypeChange('mpi')}
            className={ndtToggleCls(formData.mpiType === 'mpi')}
          >
            MPI
          </button>
          <button
            type="button"
            onClick={() => handleMpiTypeChange('fluorescent')}
            className={ndtToggleCls(formData.mpiType === 'fluorescent')}
          >
            Fluorescent
          </button>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={ndtLabelCls}>Testdato</label>
            <input type="date" required value={formData.testDate} onChange={(e) => setFormData((p) => ({ ...p, testDate: e.target.value }))} className={ndtInputCls} />
          </div>
          <div>
            <label className={ndtLabelCls}>Teststed</label>
            <select
              value={formData.testSite}
              onChange={(e) => setFormData((p) => ({ ...p, testSite: e.target.value }))}
              className={ndtSelectCls}
            >
              {MT_TEST_SITES.map((site) => (
                <option key={site} value={site}>{site}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={ndtLabelCls}>Kunde</label>
            <select value={formData.company || ''} onChange={(e) => void handleCompanyChange(e.target.value)} className={ndtSelectCls}>
              <option value="">Velg kunde</option>
              {companies.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className={ndtLabelCls}>Vårt ordrenummer</label>
            <input
              type="text"
              required
              value={formData.reportNumber}
              onChange={(e) => setFormData((p) => ({ ...p, reportNumber: e.target.value }))}
              className={ndtInputCls}
              placeholder="STM ordrenummer"
            />
          </div>
        </div>

        <NdtEquipmentSearch
          key={formData.company || 'none'}
          companyId={formData.company || ''}
          onSelect={handleSelectEquipment}
        />

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={ndtLabelCls}>Objektbeskrivelse</label>
            <input
              type="text"
              required
              value={formData.objectDescription}
              onChange={(e) => setFormData((p) => ({ ...p, objectDescription: e.target.value }))}
              placeholder="f.eks. Wire rope sling"
              className={ndtInputCls}
            />
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1">
              <label className="flex items-center cursor-pointer min-h-[40px]">
                <input
                  type="radio"
                  checked={formData.extentOfExaminationType === 'spec'}
                  onChange={() => setFormData((p) => ({ ...p, extentOfExaminationType: 'spec', extentOfExamination: 'Acc. to spec' }))}
                  className="mr-2 w-5 h-5 accent-[#1E3A5F]"
                />
                <span className="text-sm text-slate-700">Acc. to spec</span>
              </label>
              <div className="flex items-center gap-2 min-h-[40px]">
                <label className="flex items-center cursor-pointer shrink-0">
                  <input
                    type="radio"
                    checked={formData.extentOfExaminationType === 'percentage'}
                    onChange={() => setFormData((p) => ({
                      ...p,
                      extentOfExaminationType: 'percentage',
                      extentOfExamination: p.extentOfExamination === 'Acc. to spec' ? '100' : p.extentOfExamination,
                    }))}
                    className="mr-2 w-5 h-5 accent-[#1E3A5F]"
                  />
                  <span className="text-sm text-slate-700">Prosent</span>
                </label>
                <input
                  type="text"
                  inputMode="decimal"
                  value={formData.extentOfExaminationType === 'percentage' ? formData.extentOfExamination : ''}
                  onChange={(e) => setFormData((p) => ({
                    ...p,
                    extentOfExaminationType: 'percentage',
                    extentOfExamination: e.target.value,
                  }))}
                  className="w-16 min-h-[44px] px-2 text-sm text-center border border-slate-300 rounded-md focus:outline-none focus:border-[#1E3A5F] text-slate-800 bg-white placeholder:text-slate-400"
                  placeholder="%"
                />
              </div>
            </div>
          </div>
          <div>
            <label className={ndtLabelCls}>Objekt-ID</label>
            {formData.objects.map((obj, index) => (
              <div key={index} className="flex gap-2 mb-2">
                <input
                  type="text"
                  value={obj}
                  onChange={(e) => handleObjectChange(index, e.target.value)}
                  placeholder={`Objekt ${index + 1}`}
                  className={`${ndtInputCls} flex-1`}
                />
                {formData.objects.length > 1 && (
                  <button type="button" onClick={() => setFormData((p) => ({ ...p, objects: p.objects.filter((_, i) => i !== index) }))} className="p-2 text-red-600 hover:bg-red-50 rounded-md">
                    <X size={16} />
                  </button>
                )}
              </div>
            ))}
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
              <button type="button" onClick={() => setFormData((p) => ({ ...p, objects: [...p.objects, ''] }))} className="inline-flex items-center gap-2 text-sm text-[#0D9488] font-medium min-h-[44px]">
                <Plus size={16} /> Legg til objekt
              </button>
              <label className="inline-flex items-center gap-2 cursor-pointer min-h-[44px]">
                <input
                  type="checkbox"
                  checked={showDrawing}
                  onChange={(e) => {
                    const checked = e.target.checked
                    setShowDrawing(checked)
                    if (!checked) setFormData((p) => ({ ...p, drawing: '' }))
                  }}
                  className="w-5 h-5 accent-[#1E3A5F] rounded"
                />
                <span className="text-sm font-semibold text-slate-800">Tegning</span>
              </label>
            </div>
            {showDrawing && (
              <input
                type="text"
                value={formData.drawing || ''}
                onChange={(e) => setFormData((p) => ({ ...p, drawing: e.target.value }))}
                placeholder="Tegningsnummer"
                className={`${ndtInputCls} mt-1`}
              />
            )}
          </div>
        </div>

        {showCustomFields && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {customFields?.require_customer_project && (
              <div>
                <label className={ndtLabelCls}>Kundeprosjekt</label>
                <input type="text" value={formData.customerProject || ''} onChange={(e) => setFormData((p) => ({ ...p, customerProject: e.target.value }))} className={ndtInputCls} />
              </div>
            )}
            {customFields?.require_hull_yard && (
              <div>
                <label className={ndtLabelCls}>Hull/Yard</label>
                <input type="text" value={formData.hullYard || ''} onChange={(e) => setFormData((p) => ({ ...p, hullYard: e.target.value }))} placeholder={customFields.hull_yard_default || 'Ukjent/Unknown'} className={ndtInputCls} />
              </div>
            )}
            {customFields?.require_charge && (
              <div>
                <label className={ndtLabelCls}>Charge</label>
                <input type="text" value={formData.charge || ''} onChange={(e) => setFormData((p) => ({ ...p, charge: e.target.value }))} className={ndtInputCls} />
              </div>
            )}
          </div>
        )}

        {isAdmin && (
          <div>
            <label className={ndtLabelCls}>Utført av</label>
            <select
              required
              value={formData.issuedBy}
              onChange={(e) => setFormData((p) => ({ ...p, issuedBy: e.target.value }))}
              className={ndtSelectCls}
            >
              <option value="">Velg inspektør</option>
              {inspectors.map((i) => (
                <option key={i.id} value={i.full_name}>{i.full_name}</option>
              ))}
            </select>
          </div>
        )}

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={ndtLabelCls}>Testprosedyre</label>
            <select value={formData.testProcedure || ''} onChange={(e) => setFormData((p) => ({ ...p, testProcedure: e.target.value }))} className={ndtSelectCls}>
              {(availableProcedures.length > 0
                ? availableProcedures.map((procedure) => procedure.procedure_name)
                : [formData.testProcedure || 'MT DNV-CG-0051']
              ).map((name) => (
                <option key={name} value={name}>{name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className={ndtLabelCls}>Ordreansvarlig</label>
            <select value={formData.verifiedBy || ''} onChange={(e) => setFormData((p) => ({ ...p, verifiedBy: e.target.value }))} className={ndtSelectCls}>
              <option value="">Ingen</option>
              {admins.map((a) => (
                <option key={a.id} value={a.full_name}>{a.full_name}</option>
              ))}
            </select>
          </div>
        </div>

        <NdtStatusButtons
          value={formData.status}
          onChange={(status) => setFormData((p) => ({ ...p, status }))}
        />

        <div>
          <label className={ndtLabelCls}>
            Kommentarer
            {formData.status !== 'Not Accepted' && (
              <span className="normal-case tracking-normal font-normal text-slate-300"> (valgfritt)</span>
            )}
          </label>
          <textarea
            rows={2}
            value={formData.comments || ''}
            onChange={(e) => setFormData((p) => ({ ...p, comments: e.target.value }))}
            placeholder={formData.status === 'Not Accepted' ? 'Beskriv årsaken til at rapporten er underkjent' : 'Tilleggskommentarer…'}
            className={ndtTextareaCls}
          />
        </div>

        <div>
          <label className={ndtLabelCls}>Bilde (valgfritt)</label>
          {!formData.attachedImage ? (
            <label className={ndtUploadCls}>
              <Upload size={16} className="text-slate-400" />
              <span className="text-sm text-slate-600">Last opp bilde</span>
              <input type="file" accept="image/*" onChange={handleImageUpload} className="hidden" />
            </label>
          ) : (
            <div className="relative">
              <img src={formData.attachedImage} alt="Vedlegg" className="w-full h-64 object-cover rounded-md border border-slate-200" />
              <button type="button" onClick={() => setFormData((p) => ({ ...p, attachedImage: undefined }))} className="absolute top-2 right-2 p-2 bg-red-600 text-white rounded-full">
                <X size={20} />
              </button>
            </div>
          )}
        </div>

        <NdtFormActions>
          <button
            type="button"
            onClick={() => void handlePreview()}
            disabled={isSubmitting || isGeneratingPreview}
            className={ndtSecondaryBtnCls}
          >
            {isGeneratingPreview ? 'Genererer…' : 'Forhåndsvis'}
          </button>
          <button
            type="submit"
            disabled={isSubmitting || isGeneratingPreview || !canSubmit}
            className={ndtPrimaryBtnCls}
          >
            {isSubmitting ? 'Genererer rapport...' : 'Generer rapport'}
          </button>
        </NdtFormActions>
      </form>
    </NdtFormPage>
  )
}

export default MTTestForm
